import { Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    updateAvailableOrgList,
    updateEntitySteps,
} from '../../../redux/slices/entityInstanceSlice'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import {
    exportTemplateForBulkUpload,
    importBulkUploadFromExcel,
} from '../../../services/uploadManagerService'
import {
    BulkUploadDialog,
    BulkUploadDownloadActions,
    BulkUploadDropzone,
    BulkUploadSelect,
    BulkUploadUploadActions,
} from './BulkUploadUI'
import { BulkUploadModalProps, Template } from './interface'
import ProgramSelector from './ProgramSelector'
import { useBulkUploadDropzone } from './useBulkUploadDropzone'
import { getAutoSelectedTemplateId, isProgramSelectionMandatory } from './utils'

const EntityBulkUploadModal = ({
    open,
    onClose,
    templates: propTemplates,
    initialTemplateId = '',
    mode = 'both',
    showSubTypes = true,
}: BulkUploadModalProps): React.ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const availableOrgList = useAppSelector(
        (state) => state.entityInstance.availableOrgList,
    )
    const loggedInOrgId = useAppSelector((state) => state.org.org?.id)
    const [uploadSuccess, setUploadSuccess] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [selectedProgramIdForUpload, setSelectedProgramIdForUpload] =
        useState('')
    const [selectedProgramIdForDownload, setSelectedProgramIdForDownload] =
        useState('')
    const [selectedTemplateForUpload, setSelectedTemplateForUpload] =
        useState(initialTemplateId)
    const [selectedSubTemplateForUpload, setSelectedSubTemplateForUpload] =
        useState('')
    const [selectedTemplateForSave, setSelectedTemplateForSave] =
        useState(initialTemplateId)
    const [selectedSubTemplateForSave, setSelectedSubTemplateForSave] =
        useState('')

    const uploadTemplates = selectedProgramIdForUpload
        ? (availableOrgList.find((org) => org.id === selectedProgramIdForUpload)
              ?.templates as Template[]) || []
        : propTemplates

    const downloadTemplates = selectedProgramIdForDownload
        ? (availableOrgList.find(
              (org) => org.id === selectedProgramIdForDownload,
          )?.templates as Template[]) || []
        : propTemplates

    useEffect(() => {
        if (open) {
            setSelectedFile(null)
            setSelectedProgramIdForUpload('')
            setSelectedProgramIdForDownload('')

            let uploadAutoSelected = ''
            let downloadAutoSelected = ''

            if (!initialTemplateId && propTemplates) {
                uploadAutoSelected = getAutoSelectedTemplateId(propTemplates)
                downloadAutoSelected = getAutoSelectedTemplateId(propTemplates)
            }

            setSelectedTemplateForUpload(
                initialTemplateId || uploadAutoSelected,
            )
            setSelectedSubTemplateForUpload('')

            setSelectedTemplateForSave(
                initialTemplateId || downloadAutoSelected,
            )
            setSelectedSubTemplateForSave('')
        }
    }, [open, initialTemplateId, propTemplates])

    const resetModalState = useCallback(() => {
        setSelectedFile(null)
        setSelectedProgramIdForUpload('')
        setSelectedProgramIdForDownload('')
        setSelectedTemplateForUpload('')
        setSelectedSubTemplateForUpload('')
        setSelectedTemplateForSave('')
        setSelectedSubTemplateForSave('')

        dispatch(updateAvailableOrgList([]))
        dispatch(updateEntitySteps([]))
    }, [dispatch])

    const getSubEntities = useCallback(
        (templateId: string, templateList: Template[]) => {
            const template = templateList.find((t) => t.id === templateId)
            return template?.subEntityTypes || []
        },
        [],
    )

    const { getRootProps, getInputProps, isDragActive } =
        useBulkUploadDropzone(setSelectedFile)

    const handleUpload = useCallback(async () => {
        const finalTemplateId =
            selectedTemplateForUpload || selectedSubTemplateForUpload
        if (!selectedFile || !finalTemplateId) return

        const isProgram = !!selectedProgramIdForUpload

        dispatch(updateLoader(true))
        try {
            const result = await importBulkUploadFromExcel(
                selectedFile,
                loggedInOrgId || '',
                finalTemplateId,
                isProgram,
                selectedProgramIdForUpload,
            )

            if (result.type === 'success') {
                setUploadSuccess(true)
                setSelectedFile(null)
                setSelectedSubTemplateForUpload('')

                setTimeout(() => {
                    setUploadSuccess(false)
                    resetModalState()
                    onClose()
                })
            }
        } finally {
            dispatch(updateLoader(false))
        }
    }, [
        selectedFile,
        selectedSubTemplateForUpload,
        selectedTemplateForUpload,
        loggedInOrgId,
        selectedProgramIdForUpload,
        dispatch,
        resetModalState,
        onClose,
    ])

    const handleSave = useCallback(async () => {
        const finalTemplateId = selectedTemplateForSave
        if (!finalTemplateId) return

        const isProgram = !!selectedProgramIdForDownload

        dispatch(updateLoader(true))
        try {
            await exportTemplateForBulkUpload(
                finalTemplateId,
                loggedInOrgId || '',
                isProgram,
                selectedProgramIdForDownload,
            )
            setSelectedSubTemplateForSave('')
            resetModalState()
            onClose()
        } finally {
            dispatch(updateLoader(false))
        }
    }, [
        selectedTemplateForSave,
        loggedInOrgId,
        selectedProgramIdForDownload,
        dispatch,
        resetModalState,
        onClose,
    ])

    const subEntitiesForUpload = getSubEntities(
        selectedTemplateForUpload,
        uploadTemplates,
    )

    const isProgramSelectionMandatoryForUpload = useMemo(() => {
        return isProgramSelectionMandatory(
            availableOrgList,
            selectedProgramIdForUpload,
        )
    }, [availableOrgList, selectedProgramIdForUpload])

    const isProgramSelectionMandatoryForDownload = useMemo(() => {
        return isProgramSelectionMandatory(
            availableOrgList,
            selectedProgramIdForDownload,
        )
    }, [availableOrgList, selectedProgramIdForDownload])

    return (
        <BulkUploadDialog
            open={open}
            onClose={() => {
                resetModalState()
                onClose()
            }}
            title={t('uploadManager.bulkUpload.title')}
            closeLabel={t('close')}
        >
            {/* Upload Section */}
            {(mode === 'upload' || mode === 'both') && (
                <>
                    {availableOrgList?.length > 1 && (
                        <ProgramSelector
                            selectedProgramId={selectedProgramIdForUpload}
                            onProgramChange={(newProgramId) => {
                                setSelectedProgramIdForUpload(newProgramId)
                                const templatesForNewProgram =
                                    availableOrgList.find(
                                        (org) => org.id === newProgramId,
                                    )?.templates || []
                                const autoSelected = getAutoSelectedTemplateId(
                                    templatesForNewProgram,
                                )
                                setSelectedTemplateForUpload(autoSelected)
                                setSelectedSubTemplateForUpload('')
                            }}
                            availableOrgList={availableOrgList}
                        />
                    )}
                    {/* Template Selection for Upload */}
                    {uploadTemplates.length > 1 && (
                        <BulkUploadSelect
                            id="upload-template-select"
                            label={t(
                                'uploadManager.bulkUpload.selectTemplateForUpload',
                            )}
                            value={selectedTemplateForUpload}
                            onChange={(e) => {
                                const newTemplateId = e.target.value as string
                                setSelectedTemplateForUpload(newTemplateId)
                                const selectedTemplate = uploadTemplates.find(
                                    (t) => t.id === newTemplateId,
                                )
                                if (
                                    selectedTemplate?.subEntityTypes &&
                                    selectedTemplate?.subEntityTypes?.length > 0
                                ) {
                                    setSelectedSubTemplateForUpload(
                                        selectedTemplate?.subEntityTypes[0]?.id,
                                    )
                                } else {
                                    setSelectedSubTemplateForUpload('')
                                }
                            }}
                            options={uploadTemplates}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectTemplate',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    uploadTemplates.find(
                                        (t) => t.id === selected,
                                    )?.name
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectTemplate',
                                        )}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    {/* Sub Entity Type Selection for Upload */}
                    {showSubTypes && subEntitiesForUpload.length > 0 && (
                        <BulkUploadSelect
                            id="upload-subtype-select"
                            label={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            value={selectedSubTemplateForUpload}
                            onChange={(e) =>
                                setSelectedSubTemplateForUpload(e.target.value)
                            }
                            options={subEntitiesForUpload}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectSubEntityType',
                            )}
                            sx={{ mb: 3 }}
                        />
                    )}

                    {/* Dropzone */}
                    <BulkUploadDropzone
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        selectedFileName={selectedFile?.name}
                        selectFileLabel={t(
                            'uploadManager.bulkUpload.selectFileToUpload',
                        )}
                        dragAndDropLabel={t(
                            'uploadManager.bulkUpload.dragAndDrop',
                        )}
                    />

                    {/* Upload Button */}
                    <BulkUploadUploadActions
                        uploadSuccess={uploadSuccess}
                        successMessage={t(
                            'uploadManager.bulkUpload.fileUploadedSuccess',
                        )}
                        uploadFileLabel={t('uploadFile')}
                        onUpload={handleUpload}
                        disabled={
                            !selectedFile ||
                            !selectedTemplateForUpload ||
                            isProgramSelectionMandatoryForUpload
                        }
                    />
                </>
            )}

            {/* Download Template Section */}
            {(mode === 'download' || mode === 'both') && (
                <>
                    {availableOrgList?.length > 1 && (
                        <ProgramSelector
                            selectedProgramId={selectedProgramIdForDownload}
                            onProgramChange={(newProgramId) => {
                                setSelectedProgramIdForDownload(newProgramId)
                                const templatesForNewProgram =
                                    availableOrgList.find(
                                        (org) => org.id === newProgramId,
                                    )?.templates || []
                                const autoSelected = getAutoSelectedTemplateId(
                                    templatesForNewProgram,
                                )
                                setSelectedTemplateForSave(autoSelected)
                            }}
                            availableOrgList={availableOrgList}
                        />
                    )}
                    {downloadTemplates.length > 1 && (
                        <BulkUploadSelect
                            id="download-template-select"
                            label={t(
                                'uploadManager.bulkUpload.selectDownloadTemplate',
                            )}
                            value={selectedTemplateForSave}
                            onChange={(e) => {
                                const newTemplateId = e.target.value as string
                                setSelectedTemplateForSave(newTemplateId)
                                const selectedTemplate = downloadTemplates.find(
                                    (t) => t.id === newTemplateId,
                                )
                                if (
                                    selectedTemplate &&
                                    selectedTemplate.subEntityTypes &&
                                    selectedTemplate.subEntityTypes.length > 0
                                ) {
                                    setSelectedSubTemplateForSave(
                                        selectedTemplate.subEntityTypes[0].id,
                                    )
                                } else {
                                    setSelectedSubTemplateForSave('')
                                }
                            }}
                            options={downloadTemplates}
                            placeholder={t(
                                'uploadManager.bulkUpload.selectTemplate',
                            )}
                            renderValue={(selected) =>
                                selected ? (
                                    downloadTemplates.find(
                                        (t) => t.id === selected,
                                    )?.name
                                ) : (
                                    <Typography sx={{ color: '#9ca3af' }}>
                                        {t(
                                            'uploadManager.bulkUpload.selectTemplate',
                                        )}
                                    </Typography>
                                )
                            }
                        />
                    )}

                    {showSubTypes &&
                        getSubEntities(
                            selectedTemplateForSave,
                            downloadTemplates,
                        ).length > 0 && (
                            <BulkUploadSelect
                                id="download-subtype-select"
                                label={t(
                                    'uploadManager.bulkUpload.selectSubEntityType',
                                )}
                                value={selectedSubTemplateForSave}
                                onChange={(e) =>
                                    setSelectedSubTemplateForSave(
                                        e.target.value,
                                    )
                                }
                                options={getSubEntities(
                                    selectedTemplateForSave,
                                    downloadTemplates,
                                )}
                                placeholder={t(
                                    'uploadManager.bulkUpload.selectSubEntityType',
                                )}
                            />
                        )}

                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1, color: '#666' }}
                    >
                        {t('uploadManager.bulkUpload.downloadNote')}
                    </Typography>

                    <BulkUploadDownloadActions
                        onCancel={() => {
                            resetModalState()
                            onClose()
                        }}
                        onDownload={handleSave}
                        disabled={
                            (!selectedTemplateForSave &&
                                !selectedSubTemplateForSave) ||
                            isProgramSelectionMandatoryForDownload
                        }
                        cancelLabel={t('uploadManager.bulkUpload.cancel')}
                        downloadLabel={t(
                            'uploadManager.bulkUpload.downloadTemplate',
                        )}
                    />
                </>
            )}
        </BulkUploadDialog>
    )
}

export default EntityBulkUploadModal
